import { FC, ReactNode, createContext } from 'react'
import { useRouter } from 'next/router'
import {
  ExamSubject,
  TentativeScoringQuestionItemDesktop_ExamGradingQuestionFragment,
  useGqlExamGradingQuestionsQuery,
} from 'generated/graphql'
import DEFAULT_TEST_ID from '@app.feature/exam/gsat2024-2/constants/defaultTestId'
import {
  STEP_FIVE_STAGE,
  StepFiveStageType,
} from '../constants/stepFive.constant'

export interface IFTentativeScoringQuestionContextProviderProps {
  children: ReactNode
  onChangeStage?: (stage: StepFiveStageType) => void
  defaultTestId?: string
}

interface IFTentativeScoringQuestionContextProps {
  subject: Lowercase<ExamSubject>
  isQuestionOpen: boolean
  examGradingQuestions?: Array<TentativeScoringQuestionItemDesktop_ExamGradingQuestionFragment>
  examGradingQuestionData?: {
    math: Array<TentativeScoringQuestionItemDesktop_ExamGradingQuestionFragment>
    reasoning: Array<TentativeScoringQuestionItemDesktop_ExamGradingQuestionFragment>
  }
  handleChangeResultStage?: () => void
  handleChangeInputAnswerStage?: () => void
}

const TentativeScoringQuestionContext =
  createContext<IFTentativeScoringQuestionContextProps>({
    subject: 'math',
    isQuestionOpen: false,
  })

const TentativeScoringQuestionContextProvider: FC<
  IFTentativeScoringQuestionContextProviderProps
> = ({ onChangeStage, children, defaultTestId }) => {
  const { query } = useRouter()

  const { data } = useGqlExamGradingQuestionsQuery({
    variables: {
      mathInput: {
        subject: ExamSubject.MATH,
        testId: query.testId?.toString() || defaultTestId || DEFAULT_TEST_ID,
      },
      reasoningInput: {
        subject: ExamSubject.REASONING,
        testId: query.testId?.toString() || defaultTestId || DEFAULT_TEST_ID,
      },
    },
    fetchPolicy: 'network-only',
  })

  const handleChangeResultStage = () => {
    if (onChangeStage) {
      onChangeStage(STEP_FIVE_STAGE.RESULT)
    }
  }

  const handleChangeInputAnswerStage = async () => {
    if (onChangeStage) {
      onChangeStage(STEP_FIVE_STAGE.INPUT_ANSWER)
    }
  }

  const subject = query.subject
    ? ((query.subject as string).toLowerCase() as Lowercase<ExamSubject>)
    : 'math'

  const isQuestionOpen =
    data?.math.length === 20 && data?.reasoning.length === 30

  const initialValue: IFTentativeScoringQuestionContextProps = {
    subject,
    isQuestionOpen,
    examGradingQuestions: subject === 'math' ? data?.math : data?.reasoning,
    examGradingQuestionData: data,
    handleChangeResultStage,
    handleChangeInputAnswerStage,
  }

  return (
    <TentativeScoringQuestionContext.Provider value={initialValue}>
      {data && children}
    </TentativeScoringQuestionContext.Provider>
  )
}

export {
  TentativeScoringQuestionContext,
  TentativeScoringQuestionContextProvider,
}
